/**
 * F-treasury — /controle: Painel de Controle (Tesouraria).
 *
 * A RECEBER (cliente): em aberto + vencido + faixas de atraso (aging) + lista
 * das vencidas. A PAGAR: fornecedor, intermediário (intercompany ES→BS) e a
 * COMISSÃO por linha (técnico/intermediário) com gate ("liberado" = cliente já
 * pagou). GUARDRAIL: comissões cuja cascata não fecha o orçamento (não pagar
 * valor errado). Só leitura; escopo na empresa selecionada.
 */
import { useState } from "react";
import { Link } from "wouter";
import { CustomerStatementModal } from "../components/domain/CustomerStatementModal.js";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { trpc } from "../lib/trpc.js";
import { formatMoney } from "../lib/format-fr.js";
import { useT } from "../i18n/index.js";

const inputClass =
  "rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none";

const fmt = (n?: number) => (n == null ? "…" : formatMoney(n, { decimals: 2 }));

type Tone = "red" | "amber" | "lime" | "default";

function Stat({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: Tone;
}) {
  const valueColor =
    tone === "red"
      ? "text-red-300"
      : tone === "amber"
        ? "text-amber-300"
        : tone === "lime"
          ? "text-lime"
          : "text-neutral-100";
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-3">
      <div className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className={`mt-1 font-mono text-lg ${valueColor}`}>{value}</div>
      {sub != null && <div className="mt-0.5 text-[11px] text-neutral-500">{sub}</div>}
    </div>
  );
}

export default function Controle() {
  const t = useT();
  const { user } = useAuth();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  const enabled = companyId !== null;
  const cid = companyId ?? 0;

  const summaryQ = trpc.treasury.summary.useQuery({ companyId: cid }, { enabled });
  const recvQ = trpc.treasury.receivables.useQuery(
    { companyId: cid, filter: "overdue", limit: 200 },
    { enabled },
  );
  const issuesQ = trpc.treasury.commissionIssues.useQuery(
    { companyId: cid, limit: 100 },
    { enabled },
  );
  // F-releve-conta (CEO 2026-11-03) — A receber POR CLIENTE + relevé.
  const [recvView, setRecvView] = useState<"cliente" | "fatura">("cliente");
  const byCustQ = trpc.treasury.receivablesByCustomer.useQuery({ companyId: cid }, { enabled });
  const [releveDe, setReleveDe] = useState<number | null>(null);
  const porCliente = byCustQ.data ?? [];

  const s = summaryQ.data;
  const recv = recvQ.data ?? [];
  const issues = issuesQ.data ?? [];
  const issuesCount = s?.commissionIssues.lineCount ?? 0;

  return (
    <Shell>
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-neutral-400">
          {t("treasury.companyLabel")}{" "}
          <select
            value={companyId ?? ""}
            onChange={(e) => setCompanyId(Number(e.target.value))}
            className={inputClass}
          >
            {accessibleCompanies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        {summaryQ.isLoading && (
          <span className="text-xs text-neutral-500">{t("treasury.loading")}</span>
        )}
      </div>

      {/* ── A RECEBER ───────────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-neutral-400">
          {t("treasury.receivableCustomerTitle")}
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
          <Stat
            label={t("treasury.statOpen")}
            value={fmt(s?.receivable.customer.openTotal)}
            sub={`${s?.receivable.customer.count ?? 0} ${t("treasury.invoicesSuffix")}`}
          />
          <Stat
            label={t("treasury.statOverdue")}
            value={fmt(s?.receivable.customer.overdueTotal)}
            tone="red"
          />
          <Stat
            label={t("treasury.statNotDue")}
            value={fmt(s?.receivable.customer.aging.notDue)}
            tone="lime"
          />
          <Stat
            label={t("treasury.statAging0_30")}
            value={fmt(s?.receivable.customer.aging.d0_30)}
            tone="amber"
          />
          <Stat
            label={t("treasury.statAging31_60")}
            value={fmt(s?.receivable.customer.aging.d31_60)}
            tone="amber"
          />
          <Stat
            label={t("treasury.statAging61_90")}
            value={fmt(s?.receivable.customer.aging.d61_90)}
            tone="red"
          />
          <Stat
            label={t("treasury.statAging90plus")}
            value={fmt(s?.receivable.customer.aging.d90plus)}
            tone="red"
          />
        </div>
        {s != null && s.receivable.intercompany.count > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat
              label={t("treasury.statIntercompanyReceivable")}
              value={fmt(s.receivable.intercompany.openTotal)}
              sub={`${s.receivable.intercompany.count} ${t("treasury.invoicesSuffix")} · ${t("treasury.overdueLower")} ${fmt(s.receivable.intercompany.overdueTotal)}`}
            />
          </div>
        )}

        <div className="flex flex-wrap gap-2" data-testid="recv-view">
          {(["cliente", "fatura"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setRecvView(v)}
              className={`rounded-full border px-3 py-1 text-xs ${recvView === v ? "border-lime bg-lime/15 text-lime" : "border-neutral-700 hover:border-lime"}`}
              data-testid={`recv-view-${v}`}
            >
              {v === "cliente" ? t("treasury.viewByCustomer") : t("treasury.viewByInvoice")}
            </button>
          ))}
        </div>

        {recvView === "cliente" && (
          <div className="overflow-x-auto rounded-lg border border-neutral-800 bg-neutral-900">
            <table className="w-full text-sm" data-testid="recv-by-customer">
              <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-3 py-2 text-left">{t("treasury.colCustomer")}</th>
                  <th className="w-14 px-3 py-2 text-right">{t("treasury.colInvoices")}</th>
                  <th className="w-24 px-3 py-2 text-right">{t("treasury.colNotDue")}</th>
                  <th className="w-24 px-3 py-2 text-right">{t("treasury.col1_30")}</th>
                  <th className="w-24 px-3 py-2 text-right">{t("treasury.col31_60")}</th>
                  <th className="w-24 px-3 py-2 text-right">{t("treasury.col61_90")}</th>
                  <th className="w-24 px-3 py-2 text-right">{t("treasury.col90")}</th>
                  <th className="w-28 px-3 py-2 text-right">{t("treasury.colTotalHt")}</th>
                  <th className="w-28 px-3 py-2 text-right">{t("treasury.colTotalTtc")}</th>
                  <th className="w-24 px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {byCustQ.isLoading && (
                  <tr>
                    <td colSpan={10} className="px-3 py-6 text-center text-neutral-500">
                      {t("treasury.loading")}
                    </td>
                  </tr>
                )}
                {!byCustQ.isLoading && porCliente.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-3 py-6 text-center text-neutral-500">
                      {t("treasury.emptyByCustomer")}
                    </td>
                  </tr>
                )}
                {porCliente.map((r) => {
                  const cel = (v: number, tone: Tone) =>
                    v > 0 ? (
                      <span
                        className={
                          tone === "red" ? "text-red-300" : tone === "amber" ? "text-amber-300" : ""
                        }
                      >
                        {formatMoney(v, { decimals: 2 })}
                      </span>
                    ) : (
                      <span className="text-neutral-600">—</span>
                    );
                  return (
                    <tr
                      key={r.customerId}
                      className="transition-colors hover:bg-neutral-800/30"
                      data-testid={`recv-customer-${r.customerId}`}
                    >
                      <td className="px-3 py-2">
                        <Link
                          href={`/clientes/${r.customerId}`}
                          className="text-lime hover:underline"
                        >
                          {r.customerName}
                        </Link>
                        {r.maiorAtraso > 0 && (
                          <span className="ml-2 text-[11px] text-neutral-500">
                            {t("treasury.colMaxDelay")}: {r.maiorAtraso} {t("treasury.daysAbbrev")}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.invoices}</td>
                      <td className="px-3 py-2 text-right font-mono">{cel(r.aVencer, "lime")}</td>
                      <td className="px-3 py-2 text-right font-mono">{cel(r.d1_30, "amber")}</td>
                      <td className="px-3 py-2 text-right font-mono">{cel(r.d31_60, "amber")}</td>
                      <td className="px-3 py-2 text-right font-mono">{cel(r.d61_90, "red")}</td>
                      <td className="px-3 py-2 text-right font-mono">{cel(r.d90, "red")}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {formatMoney(r.saldoHt, { decimals: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">
                        {formatMoney(r.resteTtc, { decimals: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => setReleveDe(r.customerId)}
                          className="whitespace-nowrap rounded-md border border-lime/50 px-2 py-1 text-xs text-lime hover:bg-lime/10"
                          data-testid={`recv-statement-${r.customerId}`}
                        >
                          📄 {t("treasury.statementBtn")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {releveDe != null && (
          <CustomerStatementModal
            companyId={cid}
            customerId={releveDe}
            onClose={() => setReleveDe(null)}
          />
        )}

        {/* Lista de vencidas */}
        {recvView === "fatura" && (
          <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
            <table className="w-full text-sm">
              <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-3 py-2 text-left">{t("treasury.colInvoiceNumber")}</th>
                  <th className="px-3 py-2 text-left">{t("treasury.colCustomer")}</th>
                  <th className="w-28 px-3 py-2 text-left">{t("treasury.colDueDate")}</th>
                  <th className="w-24 px-3 py-2 text-right">{t("treasury.colDelay")}</th>
                  <th className="w-32 px-3 py-2 text-right font-mono">
                    {t("treasury.colBalance")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {recvQ.isLoading && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-neutral-500">
                      {t("treasury.loading")}
                    </td>
                  </tr>
                )}
                {!recvQ.isLoading && recv.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-neutral-500">
                      {t("treasury.emptyNoOverdue")}
                    </td>
                  </tr>
                )}
                {recv.map((r) => (
                  <tr key={r.id} className="transition-colors hover:bg-neutral-800/30">
                    <td className="px-3 py-2 font-mono text-lime">
                      {r.invoiceNumber}
                      {r.consolidated && (
                        <span className="ml-2 rounded bg-violet-500/15 px-1.5 py-0.5 align-middle text-[10px] text-violet-300">
                          {t("treasury.badgeConsolidated")}
                        </span>
                      )}
                    </td>
                    <td className="max-w-[220px] truncate px-3 py-2">{r.customerName}</td>
                    <td className="px-3 py-2 text-xs tabular-nums text-neutral-400">
                      {r.dueAt ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span
                        className={
                          r.daysLate > 90
                            ? "text-red-300"
                            : r.daysLate > 0
                              ? "text-amber-300"
                              : "text-neutral-400"
                        }
                      >
                        {r.daysLate > 0 ? `${r.daysLate} ${t("treasury.daysAbbrev")}` : "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatMoney(r.saldo, { decimals: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── A PAGAR ─────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-neutral-400">
          {t("treasury.payableTitle")}
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Stat
            label={t("treasury.statSupplierOpen")}
            value={fmt(s?.payable.supplier.openTotal)}
            sub={`${s?.payable.supplier.count ?? 0} ${t("treasury.invoicesSuffix")} · ${t("treasury.overdueLower")} ${fmt(s?.payable.supplier.overdueTotal)}`}
            tone={s != null && s.payable.supplier.overdueTotal > 0 ? "red" : "default"}
          />
          <Stat
            label={t("treasury.statIntermediaryIntercompany")}
            value={fmt(s?.payable.intermediaryIntercompany.openTotal)}
            sub={`${s?.payable.intermediaryIntercompany.count ?? 0} ${t("treasury.invoicesSuffix")} · ${t("treasury.overdueLower")} ${fmt(s?.payable.intermediaryIntercompany.overdueTotal)}`}
            tone={
              s != null && s.payable.intermediaryIntercompany.overdueTotal > 0 ? "red" : "default"
            }
          />
          <Stat
            label={t("treasury.statTechnicianReleased")}
            value={fmt(s?.payable.technicianCommission.releasedTotal)}
            sub={`${t("treasury.blockedClientNotPaid")}: ${fmt(s?.payable.technicianCommission.blockedTotal)}`}
            tone="lime"
          />
          <Stat
            label={t("treasury.statIntermediaryReleased")}
            value={fmt(s?.payable.intermediaryCommission.releasedTotal)}
            sub={`${t("treasury.blockedLabel")}: ${fmt(s?.payable.intermediaryCommission.blockedTotal)}`}
            tone="lime"
          />
        </div>
        <p className="text-[11px] text-neutral-500">
          {t("treasury.releasedExplainPre")}{" "}
          <Link href="/pagamentos-tecnicos" className="text-lime hover:underline">
            {t("treasury.techPaymentsLink")}
          </Link>
          {t("treasury.releasedExplainSuf")}
        </p>
      </section>

      {/* ── GUARDRAIL DE COMISSÃO ───────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-neutral-400">
          {t("treasury.commissionCheckTitle")}
        </h2>
        {issuesCount === 0 ? (
          <div className="rounded-lg border border-lime/30 bg-lime/5 px-4 py-3 text-sm text-lime">
            ✓ {t("treasury.allCommissionsOk")}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-red-500/40 bg-neutral-900">
            <div className="border-b border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              ⚠️ {issuesCount}{" "}
              {issuesCount === 1 ? t("treasury.lineSingular") : t("treasury.linePlural")}{" "}
              {t("treasury.cascadeNotClosingSuffix")}
              <p className="mt-1 text-xs text-red-300/80">{t("treasury.cascadeBaseHint")}</p>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-3 py-2 text-left">{t("treasury.colDossier")}</th>
                  <th className="px-3 py-2 text-right font-mono">{t("treasury.colBudget")}</th>
                  <th className="px-3 py-2 text-right font-mono">
                    {t("treasury.colExpectedBase")}
                  </th>
                  <th className="px-3 py-2 text-right font-mono">{t("treasury.colCascadeSum")}</th>
                  <th className="px-3 py-2 text-right font-mono">{t("treasury.colDiff")}</th>
                  <th className="px-3 py-2 text-right font-mono">{t("treasury.colTechnician")}</th>
                  <th className="px-3 py-2 text-right font-mono">
                    {t("treasury.colIntermediary")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {issues.map((i) => (
                  <tr key={i.lineId} className="transition-colors hover:bg-neutral-800/30">
                    <td className="px-3 py-2">
                      <Link href={`/dossiers/${i.dossierId}`} className="text-lime hover:underline">
                        {i.dossierCode ?? i.plate ?? i.vin ?? `#${i.dossierId}`}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-neutral-400">
                      {formatMoney(i.orcamento, { decimals: 2 })}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono">
                      {formatMoney(i.base, { decimals: 2 })}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatMoney(i.soma, { decimals: 2 })}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-red-300">
                      {formatMoney(i.diff, { decimals: 2 })}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-neutral-300">
                      {formatMoney(i.technicianAmount, { decimals: 2 })}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-neutral-300">
                      {formatMoney(i.intermediaryAmount, { decimals: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <div className="space-y-6">{children}</div>
    </ErpShell>
  );
}
